import React from 'react'
import { twMerge } from 'tailwind-merge'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner'

// Auto-determine text color based on background color
const textColorMap = {
    'brand-primary': 'brand-white',      // Red → White text
    'brand-secondary': 'brand-white',    // Blue → White text
    'brand-tertiary': 'brand-black',     // Yellow → Black text (not white!)
    'brand-success': 'brand-white',      // Green → White text
    'brand-pdp-banner-red': 'brand-white', // PDP Banner Red → White text
}

/**
 * Enhanced Button Component
 *
 * Simple button component using Tailwind brand colors
 *
 * @param {Object} props
 * @param {string} [props.variantColor='brand-primary'] - Color: 'brand-primary', 'brand-secondary', 'brand-tertiary', 'brand-success'
 * @param {string} [props.variantStyle='solid'] - Style: 'solid', 'outline', 'ghost', 'link'
 * @param {string} [props.size='md'] - Size: 'xs', 'sm', 'md', 'lg'
 * @param {boolean} [props.loading=false] - Show loading spinner
 * @param {string} [props.className=''] - Additional Tailwind classes
 * @param {string} [props.href] - Link href (renders as <a> tag)
 * @param {boolean} [props.openInNewTab=false] - Open link in new tab
 * @param {React.ReactNode} [props.children] - Button content
 */

// Size classes
const sizeClasses = {
    xs: 'px-[15px] py-[8px] text-xs',
    sm: 'px-[30px] h-[45px]',
    md: 'px-[35px] h-[50px]',
    lg: 'px-[40px] h-[55px]',
}

// Base button classes
const baseClasses = 'font-heading relative inline-flex items-center justify-center whitespace-nowrap rounded text-base font-bold uppercase leading-[18px] border transition-colors active:ring-4 disabled:opacity-25'

export const Button = ({
    variantColor = 'brand-primary',
    variantStyle = 'solid',
    size = 'md',
    loading = false,
    className = '',
    href,
    openInNewTab = false,
    children,
    ...restProps
}) => {
    // Auto-determine text color based on background
    const textColor = textColorMap[variantColor] || 'brand-white'

    // Build Tailwind classes
    const bgClass = `bg-${variantColor}`
    const textClass = `text-${textColor}`
    const borderClass = `border-${variantColor}`
    const hoverBgClass = `hover:bg-${variantColor}-darken`
    const hoverTextClass = `hover:text-${textColor}`
    const activeBgClass = `active:bg-${variantColor}-darken`
    const ringClass = `active:ring-${variantColor}-darken/40`

    let variantClassName = `${baseClasses} ${sizeClasses[size] || sizeClasses.md}`

    switch (variantStyle) {
        case 'solid':
            variantClassName = twMerge(variantClassName, bgClass, textClass, borderClass, hoverBgClass, hoverTextClass, 'hover:no-underline', activeBgClass, ringClass)
            break
        case 'outline':
            variantClassName = twMerge(variantClassName, 'bg-transparent', `text-${variantColor}`, borderClass, `hover:bg-${variantColor}`, hoverTextClass, 'hover:no-underline', activeBgClass, ringClass)
            break
        case 'ghost':
            variantClassName = twMerge(variantClassName, 'bg-transparent', `text-${variantColor}`, 'border-transparent', `hover:bg-${variantColor}/10`, `active:bg-${variantColor}/20`, 'hover:no-underline')
            break
        case 'link':
            variantClassName = twMerge(variantClassName, 'bg-transparent', `text-${variantColor}`, 'border-transparent', 'hover:underline')
            break
        default:
            variantClassName = twMerge(variantClassName, bgClass, textClass, borderClass, hoverBgClass, hoverTextClass, 'hover:no-underline', activeBgClass, ringClass)
            break
    }

    const finalClassName = twMerge(`${variantClassName} ${className}`)

    const linkProps = {
        ...(openInNewTab && { target: '_blank', rel: 'noopener noreferrer' }),
    }

    return href ? (
        <a
            type="button"
            href={href}
            className={finalClassName}
            {...linkProps}
            {...restProps}
        >
            {loading ? <FontAwesomeIcon icon={faSpinner} className="fa-spin mr-2" /> : children}
        </a>
    ) : (
        <button
            className={finalClassName}
            disabled={loading || restProps.disabled}
            {...restProps}
        >
            {loading ? (
                <>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin mr-2" />
                    {children}
                </>
            ) : (
                children
            )}
        </button>
    )
}

export default Button
